import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import { X, Flame, MapPin, ShieldCheck, RotateCcw, Undo2, Crown, Zap } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import BottomNav from "../components/BottomNav";
import Logo from "../components/Logo";
import { toast } from "sonner";

function Card({ profile, onSwipe, isTop }) {
    const x = useMotionValue(0);
    const rotate = useTransform(x, [-250, 250], [-20, 20]);
    const smashOpacity = useTransform(x, [0, 100], [0, 1]);
    const passOpacity = useTransform(x, [-100, 0], [1, 0]);

    const photo = profile.photos?.[0];

    const handleDragEnd = (e, info) => {
        if (info.offset.x > 120 || info.velocity.x > 600) onSwipe("like");
        else if (info.offset.x < -120 || info.velocity.x < -600) onSwipe("pass");
    };

    return (
        <motion.div
            className="absolute inset-4 bg-ink-800 border border-white/10 overflow-hidden cursor-grab active:cursor-grabbing shadow-2xl"
            drag={isTop ? "x" : false}
            dragConstraints={{ left: 0, right: 0 }}
            style={{ x, rotate }}
            onDragEnd={handleDragEnd}
            initial={{ scale: 0.95, y: 8, opacity: 0 }}
            animate={{ scale: isTop ? 1 : 0.95, y: isTop ? 0 : 8, opacity: 1 }}
            exit={{ x: x.get() > 0 ? 600 : -600, opacity: 0, transition: { duration: 0.3 } }}
            data-testid={`discover-card-${profile.user_id}`}
        >
            {photo ? (
                <img src={fileUrl(photo)} alt={profile.name} className="absolute inset-0 w-full h-full object-cover" draggable={false} />
            ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-ink-700 to-ink-900 flex items-center justify-center">
                    <span className="font-display text-6xl text-white/20">{profile.name?.[0] || "?"}</span>
                </div>
            )}

            {/* Stamps */}
            <motion.div
                className="absolute top-8 left-8 border-4 border-smash text-smash font-display text-5xl px-3 py-1 -rotate-12"
                style={{ opacity: smashOpacity }}
            >
                SMASH
            </motion.div>
            <motion.div
                className="absolute top-8 right-8 border-4 border-white/60 text-white/80 font-display text-5xl px-3 py-1 rotate-12"
                style={{ opacity: passOpacity }}
            >
                DASH
            </motion.div>

            {/* Bottom gradient + info */}
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black via-black/70 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                <div className="flex items-end gap-2">
                    <h3 className="font-display text-4xl leading-none">{profile.name?.toUpperCase()}</h3>
                    <span className="font-display text-3xl text-white/70 leading-none">{profile.age}</span>
                    {profile.verified && <ShieldCheck className="w-5 h-5 text-dash mb-1" />}
                </div>
                <div className="flex items-center gap-2 mt-1 text-white/70 text-xs uppercase tracking-widest">
                    {profile.location_city && (
                        <span className="inline-flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {profile.location_city}
                        </span>
                    )}
                    {profile.distance_km != null && <span>• {profile.distance_km} km</span>}
                </div>
                {profile.bio && <p className="mt-2 text-sm text-white/80 line-clamp-2">{profile.bio}</p>}
            </div>
        </motion.div>
    );
}

export default function Discover() {
    const nav = useNavigate();
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastSwipe, setLastSwipe] = useState(null);
    const [matchModal, setMatchModal] = useState(null);
    const [billing, setBilling] = useState(null); // {premium, swipes_remaining}
    const [paywall, setPaywall] = useState(false);

    const loadBilling = async () => {
        try {
            const { data } = await api.get("/billing/status");
            setBilling(data);
        } catch {}
    };

    const load = async () => {
        setLoading(true);
        try {
            const [{ data }] = await Promise.all([
                api.get("/discover"),
                loadBilling(),
            ]);
            setProfiles(data);
        } catch {
            toast.error("Failed to load");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const top = profiles[0];
    const next = profiles[1];

    const doSwipe = async (action) => {
        if (!top) return;
        const current = top;
        // Client-side guard: free user with 0 remaining
        if (billing && !billing.premium && billing.swipes_remaining === 0) {
            setPaywall(true);
            return;
        }
        setProfiles((p) => p.slice(1));
        setLastSwipe(current);
        try {
            const { data } = await api.post("/swipe", {
                target_user_id: current.user_id,
                action,
            });
            // Sync billing
            setBilling((b) => b ? {
                ...b,
                swipes_today: data.swipes_today,
                swipes_remaining: data.swipes_remaining,
                premium: data.premium,
            } : b);
            if (data.matched && data.match) {
                setMatchModal({ match: data.match, other: data.target });
            }
        } catch (e) {
            if (e.response?.status === 402) {
                // Put the card back and show paywall
                setProfiles((p) => [current, ...p]);
                setPaywall(true);
            }
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-pulse"><Logo size="lg" /></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col pb-20">
            <header className="px-5 pt-5 pb-3 flex items-center justify-between">
                <Logo size="sm" />
                <div className="flex items-center gap-3">
                    {billing && (
                        billing.premium ? (
                            <button
                                onClick={() => nav("/premium")}
                                data-testid="discover-premium-badge"
                                className="flex items-center gap-1 px-2 py-1 border border-dash/60 bg-dash/10 text-dash text-[10px] uppercase tracking-widest hover:bg-dash/20"
                            >
                                <Crown className="w-3 h-3" /> Smash+
                            </button>
                        ) : (
                            <button
                                onClick={() => nav("/premium")}
                                data-testid="discover-swipes-counter"
                                className={`flex items-center gap-1 px-2 py-1 border text-[10px] uppercase tracking-widest transition-colors ${
                                    billing.swipes_remaining <= 5
                                        ? "border-smash/60 text-smash bg-smash/10 hover:bg-smash/20"
                                        : "border-white/15 text-white/70 hover:border-white/30"
                                }`}
                            >
                                <Zap className="w-3 h-3" /> {billing.swipes_remaining ?? "—"}/{billing.daily_limit} left
                            </button>
                        )
                    )}
                    <button
                        onClick={load}
                        data-testid="discover-refresh-btn"
                        className="text-white/60 hover:text-white"
                    >
                        <RotateCcw className="w-4 h-4" />
                    </button>
                </div>
            </header>

            <div className="relative flex-1" style={{ minHeight: 520 }}>
                <AnimatePresence>
                    {profiles.length === 0 ? (
                        <motion.div
                            key="empty"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="absolute inset-4 border border-dashed border-white/15 flex flex-col items-center justify-center text-center px-6 text-white"
                            data-testid="discover-empty"
                        >
                            <Flame className="w-10 h-10 text-smash mb-3" />
                            <h3 className="font-display text-3xl">NOBODY LEFT.</h3>
                            <p className="text-sm text-white/60 mt-2">Come back soon or widen your filters.</p>
                            <button
                                onClick={() => nav("/profile")}
                                className="mt-5 h-10 px-4 border border-white/20 hover:border-smash text-xs uppercase tracking-widest"
                                data-testid="discover-edit-profile"
                            >
                                Edit profile
                            </button>
                        </motion.div>
                    ) : (
                        <>
                            {next && <Card key={next.user_id + "-bg"} profile={next} onSwipe={() => {}} isTop={false} />}
                            {top && <Card key={top.user_id} profile={top} onSwipe={doSwipe} isTop />}
                        </>
                    )}
                </AnimatePresence>
            </div>

            {profiles.length > 0 && (
                <div className="flex items-center justify-center gap-6 pb-5 pt-3">
                    <button
                        onClick={() => doSwipe("pass")}
                        data-testid="discover-pass-btn"
                        aria-label="Pass"
                        className="w-16 h-16 border-2 border-white/30 hover:border-white bg-ink-800 text-white flex items-center justify-center rounded-full transition-colors"
                    >
                        <X className="w-7 h-7" strokeWidth={3} />
                    </button>
                    <button
                        onClick={() => doSwipe("like")}
                        data-testid="discover-smash-btn"
                        aria-label="Smash"
                        className="w-20 h-20 bg-smash hover:bg-smash/90 text-white flex items-center justify-center rounded-full shadow-[0_0_30px_rgba(255,0,60,0.5)] transition-colors"
                    >
                        <Flame className="w-9 h-9" strokeWidth={2.5} />
                    </button>
                </div>
            )}

            <AnimatePresence>
                {paywall && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center px-6"
                        data-testid="paywall-modal"
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 30 }}
                            animate={{ scale: 1, y: 0 }}
                            className="w-full max-w-sm border-2 border-dash bg-ink-900 p-6 text-center relative"
                        >
                            <div className="inline-flex items-center gap-2 border border-dash/60 bg-dash/10 px-3 py-1 mb-4">
                                <Crown className="w-3.5 h-3.5 text-dash" />
                                <span className="text-[10px] uppercase tracking-[0.3em] text-dash font-semibold">Daily Limit Hit</span>
                            </div>
                            <h2 className="font-display text-4xl text-white mb-2 leading-none">OUT OF<br /><span className="text-smash">SWIPES.</span></h2>
                            <p className="text-sm text-white/70 mb-5">
                                You've used all 20 free swipes today. Go Smash+ for <span className="text-dash">unlimited swipes</span> and <span className="text-dash">instant messaging</span>.
                            </p>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setPaywall(false)}
                                    data-testid="paywall-later-btn"
                                    className="flex-1 h-12 border border-white/20 hover:border-white text-xs uppercase tracking-widest"
                                >
                                    Later
                                </button>
                                <button
                                    onClick={() => { setPaywall(false); nav("/premium"); }}
                                    data-testid="paywall-upgrade-btn"
                                    className="flex-1 h-12 bg-smash hover:bg-smash/90 text-white font-display text-sm uppercase tracking-widest"
                                >
                                    Go Smash+
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {matchModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center px-6"
                        data-testid="match-modal"
                    >
                        <motion.div
                            initial={{ scale: 0.85, y: 30 }}
                            animate={{ scale: 1, y: 0 }}
                            className="w-full max-w-sm border-4 border-smash bg-ink-900 p-6 text-center"
                        >
                            <h2 className="font-display text-5xl text-smash mb-1">IT'S A SMASH.</h2>
                            <p className="text-white/70 text-sm mb-5">You and {matchModal.other.name} liked each other.</p>
                            <div className="flex justify-center gap-3 mb-5">
                                <img src={fileUrl(matchModal.other.photos?.[0])} alt="" className="w-24 h-32 object-cover border border-white/10" />
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setMatchModal(null)}
                                    data-testid="match-keep-swiping-btn"
                                    className="flex-1 h-12 border border-white/20 hover:border-white text-xs uppercase tracking-widest"
                                >
                                    Keep Swiping
                                </button>
                                <button
                                    onClick={() => {
                                        const id = matchModal.match.match_id;
                                        setMatchModal(null);
                                        nav(`/chat/${id}`);
                                    }}
                                    data-testid="match-message-btn"
                                    className="flex-1 h-12 bg-smash hover:bg-smash/90 text-white font-display text-sm uppercase tracking-widest"
                                >
                                    Say Hi
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <BottomNav />
        </div>
    );
}
